import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import autodesk_logo from 'assets/Ui/AutoDesk-HomeLogo.png';
import lumion_logo from 'assets/Ui/lumion-logo.png';
import rhino_logo from 'assets/Ui/Rhino-HomeLogo.png';
import epic_games_logo from 'assets/Ui/EpicGamesLogo.svg';
import sketch_logo from 'assets/Ui/SketchUp-HomeLogo.svg';
import d5_logo from 'assets/Ui/d5_logo.svg';
import chaos_logo from 'assets/Ui/chaos-icon-black.svg';
import pepole_icon from 'assets/Ui/people.svg';
import furniture_icon from 'assets/Ui/furniture.svg';
import transport_icon from 'assets/Ui/transport.svg';

import { useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { VerticalSpacer, HorizontalSpacer, FovModal, HubSpotFormMeeting } from 'components';
import { clearMessages, setShowOnBoarding } from 'store/actions/app';
import {
  Welcome,
  GetStartedTitle,
  HorizontalStack,
  ImageView,
  Section,
  MainSection,
  MiddleHeaderTitle,
  PreviewContainer,
  PrimeButton,
  SubscribeButton,
  Dot,
  DotsContainer,
  ButtonsSection,
  LogoContainer,
  FreeAssetsSection,
  FreeAssetsSubtitle,
  FreeAssetsContainer,
  CategoryList,
  CategoryItem,
  AssetGrid,
} from './onBoardingStyles';
import { ModelCard } from '../Home/Models/ModelCard';
import { downloadModel } from 'store/actions/rpc';
import { getSampleModels, sampleModelsGuids } from 'store/actions/userData';
import { PreviewCanvas } from 'pages';
import constants from 'constants';
import { updateCanvas } from 'utils';
import { PreviewWrapper } from './PreviewWrapper';
import WhyFovea from './whyFovea';
import { MaterialTitle } from './whyFoveaStyles';

const { CANVAS_UPDATE_TYPES, CANVAS_UPDATE_METHODS } = constants;

const OnBoarding = withTheme(props => {
  const { theme } = props;
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const [ previewURL, setPreviewURL ] = useState(null);
  const [ currentIndex, setCurrentIndex ] = useState(0);
  const [ showHubspotModal, setShowHubspotModal ] = useState(false);
  const freeModelsRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const sampleModels = useMemo(() => userDataStore.sampleModels, [ userDataStore.sampleModels ]);
  const [ freeCat, setFreeCat ] = useState('People');
  const [ hasScrolled, setHasScrolled ] = useState(false);
  const fromMyFiles = location.state?.noModels;

  const handleScroll = ref => {
    ref.scrollIntoView({
      behavior: 'smooth',
    });
  };


  useEffect(() => {
    return () => dispatch(setShowOnBoarding(false));
  }, []);

  useEffect(() => {
    dispatch(getSampleModels({}));
  }, []);

  useEffect(() => {
    convertToFile('21-25DC-A126-1369-3693-BE60-9EF9-8A93-A740-00');
  }, []);

  useEffect(() => {
    if (fromMyFiles && !hasScrolled && freeModelsRef?.current) {
      setHasScrolled(true);
      // Clear the state so that on refresh the page does not scroll again
      window.history.replaceState({}, '');
      handleScroll(freeModelsRef.current);
    }
  }, [ fromMyFiles, hasScrolled ]);

  const handleClick = () => {
    navigate('/creators');
    dispatch(setShowOnBoarding(false));
    dispatch(clearMessages());
  };

  const convertToFile = async guid => {
    if (guid) {
      const onComplete = () => dispatch({ type: 'ON_COMPLETE' });
      const args = { rpcGuid: guid, format: 'DRACO_GLB', token: null, showModal: false, onComplete };

      dispatch(downloadModel(args))
        .then(url => setPreviewURL(url))
        .catch(e => console.error(e));
    }
  };

  const handleClickPrimeButton = () => {
    window.open(
      'https://archvision.com/product/fovea/?attribute_term=Annual&attribute_plan=Basic#pricing',
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handlePeopleClick = () => {
    navigate('/creators?search_category=People');
    dispatch(setShowOnBoarding(false));
    dispatch(clearMessages());
  };

  const handleModelClick = id => {
    navigate(`/creators?attributes=free_download`, { state: { previewId: id } });
    dispatch(setShowOnBoarding(false));
    dispatch(clearMessages());
  };

  const handleFurnitureClick = () => {
    navigate('/creators?search_category=Furniture');
    dispatch(setShowOnBoarding(false));
    dispatch(clearMessages());
  };

  const handleCarClick = () => {
    navigate('/creators?search_category=Transportation');
    dispatch(setShowOnBoarding(false));
    dispatch(clearMessages());
  };
  const handleOtherClick = () => {
    navigate('/creators?search_category=Other');
    dispatch(setShowOnBoarding(false));
    dispatch(clearMessages());
  };

  const handleSubscribeClick = () => {
    window.open(
      'https://calendar.app.google/6Tr6BeQZKGys4LWbA',
      '_blank',
      'noopener,noreferrer',
    );
  };

    const updateArgs = {
      dispatch,
      type: CANVAS_UPDATE_TYPES.MATERIAL,
      method: CANVAS_UPDATE_METHODS.UPDATE,
  };

  const categories = [
    {
      label: 'People',
      icon: pepole_icon,
      iconSize: { w: '24px', h: '20px' },
      onClick: handlePeopleClick,
      models: sampleModelsGuids.people,
    },
    {
      label: 'Transportation',
      icon: transport_icon,
      iconSize: { w: '30px', h: '20px' },
      onClick: handleCarClick,
      models: sampleModelsGuids.transportation,
    },
    {
      label: 'Furniture',
      icon: furniture_icon,
      iconSize: { w: '24px', h: '24px' },
      onClick: handleFurnitureClick,
      models: sampleModelsGuids.furniture,
    },
    {
      label: 'Others',
      icon: null,
      iconSize: null,
      onClick: handleOtherClick,
      models: sampleModelsGuids.other,
    },
  ];

  const modelColors = [
    '#7495D3', // Default
    '#983995', // AV Primary Purpule
    '#F6861F', // AV Dark Orange
    '#FEF491', // Pale Yellow
    '#000000', // Black
  ];

  const handleColorChange = index => {
    setCurrentIndex(index);

    const inboundData = {
      id: 'RPCmat_Plastic_Body',
      albedoColor: modelColors[index],
    };

    updateCanvas(updateArgs, inboundData);
  };

  const handleOnDownload = useCallback((rpcGuid, format, onComplete) => {
    const args = { rpcGuid, format, download: true, onComplete };
    dispatch(downloadModel(args));
  }, []);

  const buildSampleModels = rpcGuids => {
    const filteredModels = rpcGuids?.length
    ? sampleModels?.filter(model => rpcGuids.includes(model.rpc_guid))
    : sampleModels;

    return filteredModels?.map((model, index) => (
      <ModelCard
        key={index}
        job={model}
        onDownload={handleOnDownload}
        cardScale={60}
        onCardClick={() => handleModelClick(model['rpc_guid'])}
        isCardView={true}
        cardSelected={false}
        margin='0'
      />
    ));
  };

  return (
    <Welcome theme={theme} $selectedTheme={selectedTheme}>
      <MainSection>
        <VerticalSpacer size={72} />
        <HorizontalStack >
        <Section h='319px'>
            <GetStartedTitle  $selectedTheme={selectedTheme}>
              3D Assets for<br/>
              Everywhere You<br />
              Work
            </GetStartedTitle>
            <VerticalSpacer size={43} />
          </Section>
          <HorizontalSpacer size={50} />
          <Section h='319px'>
            {previewURL !== null && (
              <PreviewWrapper>
                <PreviewContainer>
                  <PreviewCanvas previewURL={previewURL} />
                </PreviewContainer>
              </PreviewWrapper>
            )}
            {previewURL !== null && (
              <DotsContainer>
                {modelColors.map((_, index) => (
                  <Dot
                    $selectedTheme={selectedTheme}
                    color={modelColors[index]}
                    key={index} active={index === currentIndex}
                    onClick={() => handleColorChange(index)} />
                ))}
              </DotsContainer>
            )}
          </Section>
          <VerticalSpacer size={40} />
        </HorizontalStack >
        <VerticalSpacer size={80} />
        <MiddleHeaderTitle theme={theme} $selectedTheme={selectedTheme}>
          Empower your 3D library for the platforms and renderers that matter to you.
        </MiddleHeaderTitle>
        <VerticalSpacer size={43} />
        <LogoContainer>
          <HorizontalStack>
            <ImageView $selectedTheme={selectedTheme} image={autodesk_logo} w='143px' h='81px' />
            <HorizontalSpacer size={30} />
            <ImageView $selectedTheme={selectedTheme} image={chaos_logo} w='80px' h='80px' />
            <HorizontalSpacer size={30} />
            <ImageView $selectedTheme={selectedTheme} image={epic_games_logo} w='142px' h='80px' />
            <HorizontalSpacer size={30} />
            <ImageView $selectedTheme={selectedTheme} image={rhino_logo} w='142px' h='80px' />
            <HorizontalSpacer size={30} />
            <ImageView $selectedTheme={selectedTheme} image={sketch_logo} w='142px' h='80px' />
            <HorizontalSpacer size={30} />
            <ImageView $selectedTheme={selectedTheme} image={d5_logo} w='143px' h='80px' />
             <HorizontalSpacer size={30} />
             <Section w='108px' h='21px' offsetY='28px'>
                <ImageView
                $selectedTheme={selectedTheme}
                image={lumion_logo}
                w='108px' h='21px' />
            </Section>
          </HorizontalStack>
        </LogoContainer>
        <VerticalSpacer size={70} />
        <ButtonsSection>
          <div>
            <PrimeButton
              theme={theme}
              $selectedTheme={selectedTheme}
              onClick={handleClickPrimeButton}
            >
              Start free trial
            </PrimeButton>
          </div>
          <HorizontalSpacer size={15} />
          <div>
            <SubscribeButton
              theme={theme}
              $selectedTheme={selectedTheme}
              onClick={handleSubscribeClick}
            >
              Schedule&nbsp;demo
            </SubscribeButton>
          </div>
        </ButtonsSection >
        <VerticalSpacer size={100} />
        <WhyFovea theme={theme} $selectedTheme={selectedTheme}/>
        <VerticalSpacer size={25} />
        <FreeAssetsSection ref={freeModelsRef}>
          <MaterialTitle theme={theme} $selectedTheme={selectedTheme}>
            Try Free Creator Assets
          </MaterialTitle>
          <VerticalSpacer size={25} />
          <FreeAssetsSubtitle $selectedTheme={selectedTheme}>
           Get your library started instantly with our free Marketplace assets.
           Additional assets are available for purchase or as part of your subscription.
          </FreeAssetsSubtitle>
          <FreeAssetsContainer>
            <CategoryList>
              {categories.map(({ label, icon, iconSize }) => (
                <CategoryItem
                  key={label}
                  active={freeCat === label}
                  $selectedTheme={selectedTheme}
                  onClick={() => setFreeCat(label)}
                >
                  {icon && (
                    <ImageView
                      $selectedTheme={selectedTheme}
                      image={icon}
                      w={iconSize.w}
                      h={iconSize.h}
                    />
                  )}
                  <HorizontalSpacer size={8} />
                  {label}
                </CategoryItem>
              ))}
            </CategoryList>
            <AssetGrid>
              {buildSampleModels(categories.find(c => c.label === freeCat)?.models.slice(0, 6))}
            </AssetGrid>
          </FreeAssetsContainer>
          <VerticalSpacer size={35} />
          <PrimeButton $selectedTheme={selectedTheme} onClick={handleClick}>
            Explore All
          </PrimeButton>
          <VerticalSpacer size={50} />
        </FreeAssetsSection>
      </MainSection>
      <FovModal
        title='Schedule a meeting'
        visible={showHubspotModal}
        onClose={() => setShowHubspotModal(false)}
        maxHeight={48 * 16}
        top={0.25}
        headerSpace={0}
      >
        <HubSpotFormMeeting />
      </FovModal>
    </Welcome>
  );
});

export default OnBoarding;
